import {createContext, ReactNode, useContext} from 'react';
import {captureException, captureMessage} from '@sentry/browser';
import {
  Errors,
  ErrorScope,
  Metrics,
  MetricTags,
  initObservability,
  Observability,
  ObservabilityConfig,
  Analytics,
} from '@stripe-internal/conversations-client';
import {MetricsParams} from '../../generated/types';
import {devLog} from '../helpers/dev';
import {supportFetch} from '../helpers/supportFetch';
import {
  AnalyticsProvider as CoreAnalyticsProvider,
  useAnalytics,
} from '../helpers/analytics';
import {ParentMessageBusContext} from './ParentMessageBusProvider';
import {isDevelopment} from '../helpers/env';

function recordMetric(metric: MetricsParams): void {
  devLog('recordMetric', metric);
  supportFetch('/api/metrics', {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'X-Requested-With': 'fetch'},
    body: JSON.stringify(metric),
  });
}

type NormalizedErrorScope = ErrorScope & {
  // ErrorScope defines this as "extras" plural
  extra?: Record<string, unknown>;
};

const NO_ERROR_ID = 'NO_ERROR_ID';

const errors: Partial<Errors> = {
  reportError: (error: string, scope: ErrorScope = {}): string => {
    // normalizing extras param
    const {extras, ...rest} = scope;
    const normalizedScope: NormalizedErrorScope = {...rest};
    if (extras) {
      normalizedScope.extra = extras;
    }

    devLog('reportError', error, normalizedScope);
    if (isDevelopment()) {
      return NO_ERROR_ID;
    }

    try {
      const eventId = captureException(new Error(error), normalizedScope);
      return eventId;
    } catch {
      return NO_ERROR_ID;
    }
  },
  reportWarning: (error: string, scope: ErrorScope = {}) => {
    // normalizing extras param
    const {extras, ...rest} = scope;
    const normalizedScope: NormalizedErrorScope = {...rest};
    if (extras) {
      normalizedScope.extra = extras;
    }

    devLog('reportWarning', error, normalizedScope);
    if (isDevelopment()) {
      return;
    }

    try {
      captureMessage(error, {level: 'warning', ...normalizedScope});
    } catch {}
  },
};

const metrics: Partial<Metrics> = {
  increment: (metric: string, tags?: MetricTags) => {
    recordMetric({
      operation: 'count',
      metric: metric as MetricsParams['metric'],
      value: 1,
      tags,
    });
  },
  count: (metric: string, value: number, tags?: MetricTags) => {
    recordMetric({
      operation: 'count',
      metric: metric as MetricsParams['metric'],
      value,
      tags,
    });
  },
  timing: (metric: string, value: number, tags?: MetricTags) => {
    recordMetric({
      operation: 'timing',
      metric: metric as MetricsParams['metric'],
      value,
      tags,
    });
  },
};

const baseObservabilityConfig: ObservabilityConfig = {
  errors,
  metrics,
};

const baseObservability = initObservability(baseObservabilityConfig);

type Props = {
  children: ReactNode;
};

const ObservabilityContext = createContext<Observability>(baseObservability);

function Provider({children}: Props) {
  const coreAnalytics = useAnalytics();
  const {requestAnalyticsEventId} = useContext(ParentMessageBusContext);

  const analytics: Analytics = {
    action: (eventName: string, parameters?: Record<string, unknown>) => {
      devLog('action', eventName, parameters);
      coreAnalytics.action(eventName, parameters);
    },
    viewed: (eventName: string, parameters?: Record<string, unknown>) => {
      devLog('viewed', eventName, parameters);
      coreAnalytics.viewed(eventName, parameters);
    },
    track: (eventName: string, parameters?: Record<string, unknown>) => {
      devLog('track', eventName, parameters);
      coreAnalytics.track(eventName, parameters);
    },
    requestAnalyticsEventId,
  };

  const observability = {
    ...baseObservability,
    analytics,
  };

  return (
    <ObservabilityContext.Provider value={observability}>
      {children}
    </ObservabilityContext.Provider>
  );
}

export function ObservabilityProvider({children}: Props) {
  return (
    <CoreAnalyticsProvider>
      <Provider>{children}</Provider>
    </CoreAnalyticsProvider>
  );
}

export type AnalyticsParamsInjector = (
  params?: Record<string, unknown>,
) => Record<string, unknown> | undefined;

export function useObservability(
  paramsInjector?: AnalyticsParamsInjector,
): Observability {
  const observability = useContext(ObservabilityContext);
  const {analytics} = observability;
  const injectParams = paramsInjector || ((params) => params);

  return {
    ...observability,
    analytics: {
      ...analytics,
      action: (eventName: string, parameters?: Record<string, unknown>) => {
        analytics.action(eventName, injectParams(parameters));
      },
      viewed: (eventName: string, parameters?: Record<string, unknown>) => {
        analytics.viewed(eventName, injectParams(parameters));
      },
      track: (eventName: string, parameters?: Record<string, unknown>) => {
        analytics.track(eventName, injectParams(parameters));
      },
    },
  };
}

export function useMetrics(): Metrics {
  return useObservability().metrics;
}
